<template>
    <div class="app-updates-page">
        <div class="app-updates-page__shell">
            <div class="app-updates-page__header">
                <div>
                    <span class="app-updates-page__eyebrow">Produit</span>
                    <h5 class="title app-updates-page__title">
                        {{ $tl("labels.appUpdates.list") }}
                        <show-new-badge-details badge_uuid="ceee56bb-d4a8-4b2d-9cc3-d90792bd71c0" />
                    </h5>
                    <p class="app-updates-page__subtitle">Retrouvez les dernières évolutions publiées sur la plateforme.</p>
                </div>
            </div>

            <LoaderComponent v-if="loading" />
            <div v-else-if="appUpdatesList.length > 0" class="app-updates-page__list">
                <article
                    v-for="appUpdate in appUpdatesList"
                    :key="appUpdate.id"
                    class="app-update-card"
                    role="button"
                    tabindex="0"
                    @click="showAppUpdate(appUpdate)"
                    @keydown.enter.prevent="showAppUpdate(appUpdate)"
                    @keydown.space.prevent="showAppUpdate(appUpdate)">
                    <div class="app-update-card__header">
                        <span class="app-update-card__date">{{ displayDate(appUpdate.update_date, DATE_MED) }}</span>
                        <h5>{{ $t("labels.appUpdates.title", { date: displayDate(appUpdate.update_date, DATE_MED) }) }}</h5>
                    </div>
                    <div class="app-update-card__description" v-html="formatDescription(appUpdate.description)"></div>
                    <div class="app-update-card__modules">
                        <span v-for="tmpModule in appUpdate.modules" :key="tmpModule" class="app-update-card__module-pill">
                            {{ getModuleTypeLabel(tmpModule) }}
                        </span>
                    </div>
                    <small class="app-update-card__meta">
                        {{ $t("labels.appUpdates.lastUpdate", { date: displayDate(appUpdate.updated_at, DATE_MED) }) }}
                    </small>
                </article>
            </div>
            <div v-else class="app-updates-page__empty">
                <p>{{ $tl("labels.appUpdates.noOnePublished") }}</p>
            </div>
        </div>
    </div>
</template>
<script>
import LoaderComponent from "../../../components/LoaderComponent.vue";
import ModuleTypesEnum from "../../../mixins/enums/ModuleTypesEnum.js";
import showNewBadgeDetails from "../../../components/NewBadges/showNewBadgeDetails.vue";

export default {
    name: "AppUpdatesList",
    data() {
        return {
            loading: false,
            appUpdatesList: [],
        };
    },
    mixins: [ModuleTypesEnum],
    methods: {
        fetchAppUpdatesList() {
            this.loading = true;

            this.httpGet("/api/app_updates")
                .then((response) => {
                    if (response !== false) {
                        this.appUpdatesList = response.data.data;

                        this.$store.dispatch("users/markAllAppUpdatesAsRead");
                    }
                })
                .finally(() => (this.loading = false));
        },
        showAppUpdate(appUpdate) {
            this.$router.push({ name: "app_updates.show", params: { app_update_id: appUpdate.id } });
        },
        formatDescription(description) {
            return this.escapeHtml(description)
                .replace(/\n{2,}/g, "<br /><br />")
                .replace(/\n/g, "<br />");
        },
    },
    components: {
        LoaderComponent,
        showNewBadgeDetails,
    },
    created() {
        this.fetchAppUpdatesList();
    },
};
</script>

<style scoped>
.app-updates-page {
    min-height: calc(100vh - 72px);
    padding: 28px 18px 40px;
    background: linear-gradient(180deg, #f7fbfc 0%, #eef8fa 100%);
}

.app-updates-page__shell {
    max-width: 1080px;
    margin: 0 auto;
}

.app-updates-page__header {
    margin-bottom: 18px;
}

.app-updates-page__eyebrow {
    display: inline-flex;
    margin-bottom: 8px;
    color: var(--main-color-light, #30a4b7);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
}

.app-updates-page__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: #142f3c;
    font-size: 1.45rem;
    font-weight: 700;
}

.app-updates-page__subtitle {
    max-width: 620px;
    margin: 8px 0 0;
    color: #6c7f88;
    line-height: 1.5;
}

.app-updates-page__list {
    display: grid;
    gap: 14px;
}

.app-update-card {
    padding: 20px 22px;
    border: 1px solid rgba(48, 164, 183, 0.12);
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 18px 45px rgba(15, 58, 72, 0.07);
    cursor: pointer;
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.app-update-card:hover,
.app-update-card:focus {
    border-color: rgba(48, 164, 183, 0.28);
    box-shadow: 0 22px 54px rgba(15, 58, 72, 0.11);
    outline: none;
    transform: translateY(-2px);
}

.app-update-card__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
}

.app-update-card__date {
    display: inline-flex;
    margin-bottom: 0;
    color: var(--main-color-light, #30a4b7);
    font-size: 0.82rem;
    font-weight: 700;
}

.app-update-card h5 {
    margin: 0;
    color: #173746;
    font-size: 1.08rem;
    font-weight: 800;
}

.app-update-card__modules {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 7px;
    margin: 0 0 14px;
}

.app-update-card__module-pill {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 5px 10px;
    color: #2b7280;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    background: rgba(48, 164, 183, 0.08);
    border: 1px solid rgba(48, 164, 183, 0.14);
    border-radius: 9px;
}

.app-update-card__description {
    margin-bottom: 16px;
    color: #334e5a;
    line-height: 1.55;
}

.app-update-card__meta {
    color: #7b8c95;
}

.app-updates-page__empty {
    padding: 34px 20px;
    border: 1px solid rgba(48, 164, 183, 0.12);
    border-radius: 18px;
    color: #6c7f88;
    text-align: center;
    background: #fff;
    box-shadow: 0 18px 45px rgba(15, 58, 72, 0.07);
}

.app-updates-page__empty p {
    margin: 0;
}

@media (max-width: 767.98px) {
    .app-updates-page {
        padding: 20px 12px 32px;
    }
}
</style>
